import React, { useEffect, useState, FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import './SignUp.css';
import { rootURL } from "../../constants";
import { useGlobalContext } from '../../GlobalContext';
import { Form, Input, Button, Typography, Card, notification, Row, Col, Divider } from 'antd';
import { 
  LockOutlined, 
  MailOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import MicrosoftAuth from '../MicrosoftAuth';

const { Title, Text, Paragraph } = Typography;

const SignUp: FunctionComponent = () => {
  const { isMobile } = useGlobalContext();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  
  function getCookie(name: string): string | null {
    let cookieValue: string | null = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
  }

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (values.password !== values.repeatedPassword) {
        setErrorMessage('Passwords do not match');
        setLoading(false);
      } else {
        setErrorMessage('');
        
        var csrftoken = getCookie('csrftoken');
        const response = await axios.post(`${rootURL}/authentication/create-user/`, {
          password: values.password,
          repeatedPassword: values.repeatedPassword,
          email: values.email
        }, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken
          },
          withCredentials: true
        });
        
        if (response.status === 201) {
          notification.success({
            message: 'Account Created',
            description: 'Please check your email to activate your account.',
          });
          navigate('/login');
        }
      }
    } catch (error: any) {
      if (error.response) {
        if (error.response.status === 409) {
          setErrorMessage('User with this email already exists');
        } else if (error.response.status === 406) {
          setErrorMessage('Email address was flagged as disposable');
        } else {
          setErrorMessage('An unexpected error occurred');
        }
      } else {
        setErrorMessage('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        await axios.get(`${rootURL}/authentication/set-csrf-token/`, { withCredentials: true });
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };
    fetchCsrfToken();
  }, []);

  return (
    <div className="signup-page-container">
      <Row justify="center" align="middle" className="signup-row">
        <Col xs={22} sm={20} md={20} lg={20} xl={15} className="signup-form-column">
          <div className="signup-form-container">
            <div className="signup-header text-center">
              <Title level={2} className="signup-title">Welcome to Tommy</Title>
              <Paragraph className="signup-subtitle">
                Create your account to get started with Tommy Agent
              </Paragraph>
            </div>
            
            {errorMessage && (
              <div className="error-message">
                {errorMessage}
              </div>
            )}
            
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
              className="signup-form"
            >
              <div className="form-input-group">
                <label htmlFor="email" className="input-label">Email</label>
                <Form.Item
                  name="email"
                  rules={[{ required: true, message: 'Please enter your email', type: 'email' }]}
                >
                  <Input 
                    id="email"
                    prefix={<MailOutlined className="input-icon" />} 
                    placeholder="Enter your email" 
                    size="large" 
                    className="signup-input"
                    disabled={loading}
                  />
                </Form.Item>
              </div>
              
              <div className="form-input-group">
                <label htmlFor="password" className="input-label">Password</label>
                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'Please enter your password' }]}
                >
                  <Input.Password 
                    id="password"
                    prefix={<LockOutlined className="input-icon" />} 
                    placeholder="Enter your password" 
                    size="large" 
                    className="signup-input"
                    disabled={loading}
                  />
                </Form.Item>
              </div>
              
              <div className="form-input-group">
                <label htmlFor="repeatedPassword" className="input-label">Confirm Password</label>
                <Form.Item
                  name="repeatedPassword"
                  rules={[{ required: true, message: 'Please repeat your password' }]}
                >
                  <Input.Password 
                    id="repeatedPassword"
                    prefix={<LockOutlined className="input-icon" />} 
                    placeholder="Confirm your password" 
                    size="large" 
                    className="signup-input"
                    disabled={loading}
                  />
                </Form.Item>
              </div>
              
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                className="signup-button"
                icon={<ArrowRightOutlined />}
                loading={loading}
                disabled={loading}
              >
                Create Account
              </Button>
              
              <Divider style={{ color: 'rgba(255, 255, 255, 0.5)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>OR</span>
              </Divider>
              
              <Row justify="center" style={{ width: '100%' }}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <MicrosoftAuth disabled={loading} />
                </div>
              </Row>

              <div className="login-prompt text-center">
                <Text style={{ color: 'white'}}>Already have an account? </Text>
                <a href="/login" className="login-link">Log In</a>
              </div>
            </Form>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SignUp;